import {
  Filter,
  Mic,
  Mail,
  MessageSquare,
  UserCheck,
  Radio,
  ShieldAlert,
  Zap,
  Clock,
  CheckCircle2,
  ChevronsRight,
  Sparkles,
  User,
  ArrowRight,
  Activity,
  Users,
  Waypoints,
} from "lucide-react";

/* --- Data --------------------------------------------------------- */

const NODES = [
  {
    id: "node-intake",
    key: "intake",
    label: "Intake Scorer Agent",
    Icon: Filter,
    accent: "#0EA5E9",
    status: { tone: "teal", label: "Active" },
    tag: "Node 01",
    headline: "Scoring 12 new leads",
    live: true,
    rows: [
      { k: "Source", v: "Trade Show Import" },
      { k: "Method", v: "Title + source + engagement scoring" },
      { k: "Output", v: "Route to Fast-Track / 7-Day / 14-Day" },
    ],
    arrowLabel: "Scored + tagged",
  },
  {
    id: "node-voice",
    key: "voice",
    label: "Voice Agent (Vapi)",
    Icon: Mic,
    accent: "#00C2A8",
    status: { tone: "amber", label: "Active · 2 live calls", pulse: true },
    tag: "Node 02",
    headline: "Alex — AI Voice Agent",
    live: true,
    rows: [
      { k: "Calls today", v: "23", mono: true },
      { k: "Live now", v: "Dr. Priya Nair (1:42), Kevin Marsh (0:23)" },
      { k: "Voicemails queued", v: "6", mono: true },
      { k: "Escalations today", v: "1 · clinical → live rep", tone: "#F59E0B" },
    ],
    arrowLabel: "No answer → SMS · Connected → qualify",
  },
  {
    id: "node-email",
    key: "email",
    label: "Email Agent (Make.com)",
    Icon: Mail,
    accent: "#F59E0B",
    status: { tone: "teal", label: "Active" },
    tag: "Node 03",
    headline: "Sequences running: 3",
    rows: [
      { k: "Emails sent today", v: "84", mono: true },
      { k: "Open rate today", v: "37%", tone: "#F59E0B", mono: true },
      { k: "Next send", v: "14 emails in 18 min", mono: true },
      { k: "Trigger", v: "voice voicemail / day-N timer" },
    ],
    arrowLabel: "Low engagement → SMS boost · Replied → route",
  },
  {
    id: "node-sms",
    key: "sms",
    label: "SMS Agent (Make.com + Twilio)",
    Icon: MessageSquare,
    accent: "#0EA5E9",
    status: { tone: "teal", label: "Active" },
    tag: "Node 04",
    headline: "SMS sent today: 38",
    rows: [
      { k: "Open rate", v: "98%", tone: "#00C2A8", mono: true, highlight: true },
      { k: "Replies received", v: "7", mono: true },
      { k: "Booking links clicked", v: "4", mono: true },
      { k: "Re-engagement rate", v: "41%", mono: true },
    ],
    arrowLabel: "Clicked link → confirm · No reply → final email",
  },
  {
    id: "node-handoff",
    key: "handoff",
    label: "Handoff Agent",
    Icon: UserCheck,
    accent: "#22C55E",
    status: { tone: "amber", label: "Standby · 2 pending" },
    tag: "Node 05",
    headline: "Demos booked today: 6",
    win: true,
    rows: [
      { k: "CRM tasks created", v: "6", mono: true, tone: "#22C55E" },
      { k: "Reps notified", v: "Taylor R., Jordan M., Sam K." },
      { k: "Avg context package", v: "3 talking points + lead history" },
    ],
    arrowLabel: null,
  },
];

const TRACE = [
  {
    id: "trace-1",
    time: "Jun 17 · 9:02 am",
    agent: "Intake Scorer",
    accent: "#0EA5E9",
    Icon: Filter,
    action: "Scored 72/100, tagged WARM, assigned 7-Day sequence",
  },
  {
    id: "trace-2",
    time: "Jun 17 · 9:05 am",
    agent: "Email Agent",
    accent: "#F59E0B",
    Icon: Mail,
    action: 'Day 1 email sent — subject: "Quick question about device setup"',
  },
  {
    id: "trace-3",
    time: "Jun 18 · 2:14 pm",
    agent: "Email Agent",
    accent: "#F59E0B",
    Icon: Mail,
    action: "Day 1 email opened",
    tone: "muted",
  },
  {
    id: "trace-4",
    time: "Jun 19 · 10:30 am",
    agent: "Voice Agent",
    accent: "#00C2A8",
    Icon: Mic,
    action: 'Outbound call — Connected, 2:01 call, "Send me more info"',
    highlight: true,
  },
  {
    id: "trace-5",
    time: "Jun 19 · 10:32 am",
    agent: "Email Agent",
    accent: "#F59E0B",
    Icon: Mail,
    action: "Triggered — clinical overview sent automatically",
    auto: true,
  },
  {
    id: "trace-6",
    time: "Jun 20 · 9:00 am",
    agent: "SMS Agent",
    accent: "#0EA5E9",
    Icon: MessageSquare,
    action: 'Day 3 SMS sent — "Still worth 15 min, Marcus?"',
  },
  {
    id: "trace-7",
    time: "Jun 20 · 9:47 am",
    agent: "SMS Agent",
    accent: "#0EA5E9",
    Icon: MessageSquare,
    action: 'Reply received — "Sure, what times work?"',
    highlight: true,
  },
  {
    id: "trace-8",
    time: "Jun 20 · 9:47 am",
    agent: "Email Agent",
    accent: "#F59E0B",
    Icon: Mail,
    action: "Booking link auto-sent",
    auto: true,
  },
  {
    id: "trace-9",
    time: "Jun 20 · 10:12 am",
    agent: "Handoff Agent",
    accent: "#22C55E",
    Icon: UserCheck,
    action: "Demo booked. CRM task created. Rep notified.",
    final: true,
  },
];

const HEALTH_STATS = [
  { id: "hs-uptime", label: "Agent Uptime This Month", value: "99.7%", note: "All 5 agents · zero human intervention", accent: "#22C55E", Icon: Activity },
  { id: "hs-time", label: "Avg Lead-to-Demo Time", value: "3.2 days", note: "compressed from 47-day manual baseline", accent: "#00C2A8", Icon: Clock },
  { id: "hs-touches", label: "Human Touches Required", value: "0", note: "until demo is booked", accent: "#0EA5E9", Icon: Users, highlight: "Zero-touch until booking" },
];

/* --- Helpers ------------------------------------------------------ */

function Pill({ tone, label, pulse }) {
  const cls = {
    teal: "pill pill-teal",
    blue: "pill pill-blue",
    amber: "pill pill-amber",
    green: "pill pill-green",
    red: "pill pill-red",
    muted: "pill pill-muted",
  }[tone];
  return (
    <span className={cls} style={{ fontSize: 10 }}>
      <span className={`pill-dot ${pulse ? "pill-dot-pulse" : ""}`} />
      {label}
    </span>
  );
}

/* --- Sub-components ----------------------------------------------- */

function AgentNode({ node, i, isLast }) {
  const { Icon } = node;
  return (
    <div
      data-testid={node.id}
      className="flex-shrink-0 relative enter-up"
      style={{
        width: 300,
        animationDelay: `${i * 120}ms`,
      }}
    >
      {/* Card */}
      <article
        className="card-glass p-4 h-full"
        style={{
          borderTop: `3px solid ${node.accent}`,
          background: `linear-gradient(180deg, ${node.accent}0A 0%, transparent 50%), var(--bg-elevated)`,
          minHeight: 300,
          boxShadow: node.live ? `0 0 24px ${node.accent}15` : "0 1px 3px rgba(0,0,0,0.4)",
          position: "relative",
        }}
      >
        {/* Node tag */}
        <div className="flex items-center justify-between mb-3">
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 9,
              letterSpacing: "0.16em",
              color: node.accent,
              textTransform: "uppercase",
              padding: "2px 6px",
              borderRadius: 3,
              background: `${node.accent}12`,
              border: `1px solid ${node.accent}30`,
            }}
          >
            {node.tag}
          </span>
          <Pill {...node.status} />
        </div>

        {/* Icon + label */}
        <div className="flex items-center gap-2.5 mb-3">
          <div
            className="flex items-center justify-center rounded-lg relative"
            style={{
              width: 38,
              height: 38,
              background: `${node.accent}18`,
              border: `1px solid ${node.accent}40`,
            }}
          >
            <Icon size={17} color={node.accent} strokeWidth={2} />
            {node.live && (
              <span
                style={{
                  position: "absolute",
                  inset: -3,
                  borderRadius: 10,
                  border: `1.5px solid ${node.accent}30`,
                  animation: "pulse-dot 1.8s ease-out infinite",
                }}
              />
            )}
          </div>
          <div className="min-w-0">
            <div
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 700,
                fontSize: 14,
                color: "var(--text-primary)",
                letterSpacing: "-0.01em",
                lineHeight: 1.2,
              }}
            >
              {node.label}
            </div>
          </div>
        </div>

        {/* Headline */}
        <div
          className="mb-3 pb-3"
          style={{ borderBottom: `1px dashed ${node.accent}30` }}
        >
          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 9.5,
              letterSpacing: "0.12em",
              color: "var(--text-muted)",
              textTransform: "uppercase",
              marginBottom: 3,
            }}
          >
            Right now
          </div>
          <div
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 600,
              fontSize: 13.5,
              color: node.accent,
              letterSpacing: "-0.005em",
            }}
          >
            {node.headline}
          </div>
        </div>

        {/* Rows */}
        <ul className="space-y-2.5">
          {node.rows.map((r, ri) => (
            <li key={ri} className="flex items-start gap-2 min-w-0">
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 9,
                  letterSpacing: "0.12em",
                  color: "var(--text-muted)",
                  textTransform: "uppercase",
                  minWidth: 90,
                  marginTop: 2,
                }}
              >
                {r.k}
              </span>
              <span
                className="min-w-0 break-words"
                style={{
                  fontFamily: r.mono ? "'JetBrains Mono', monospace" : "Inter, sans-serif",
                  fontSize: r.mono ? 12 : 12,
                  color: r.tone || "var(--text-primary)",
                  fontWeight: r.mono ? 600 : 500,
                  lineHeight: 1.4,
                }}
              >
                {r.v}
                {r.highlight && (
                  <span
                    className="ml-1.5"
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 8.5,
                      color: "#00C2A8",
                      padding: "1px 4px",
                      borderRadius: 3,
                      background: "rgba(0, 194, 168, 0.10)",
                      border: "1px solid rgba(0, 194, 168, 0.30)",
                      letterSpacing: "0.08em",
                      verticalAlign: "middle",
                    }}
                  >
                    LEVERAGE
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      </article>

      {/* Arrow to next node */}
      {!isLast && (
        <div
          className="hidden xl:flex"
          style={{
            position: "absolute",
            right: -68,
            top: "50%",
            transform: "translateY(-50%)",
            width: 68,
            height: 80,
            zIndex: 3,
            pointerEvents: "none",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ position: "relative", width: 68, height: 80 }}>
            {/* Arrow line */}
            <svg width="68" height="80" viewBox="0 0 68 80" style={{ position: "absolute", inset: 0 }}>
              <defs>
                <linearGradient id={`grad-${node.id}`} x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor={node.accent} stopOpacity="0.9" />
                  <stop offset="100%" stopColor="#00C2A8" stopOpacity="0.9" />
                </linearGradient>
              </defs>
              <line
                x1="0" y1="40" x2="58" y2="40"
                stroke={`url(#grad-${node.id})`}
                strokeWidth="1.5"
                strokeDasharray="4 4"
              >
                <animate attributeName="stroke-dashoffset" from="0" to="-16" dur="1.4s" repeatCount="indefinite" />
              </line>
              <path
                d="M54 34 L64 40 L54 46"
                stroke="#00C2A8"
                strokeWidth="1.5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {/* Traveling dot */}
              <circle r="3" fill="#00C2A8" style={{ filter: "drop-shadow(0 0 4px #00C2A8)" }}>
                <animateMotion dur="2.2s" repeatCount="indefinite" path="M0,40 L58,40" />
              </circle>
            </svg>
            {/* Arrow label */}
            {node.arrowLabel && (
              <div
                style={{
                  position: "absolute",
                  bottom: -4,
                  left: "50%",
                  transform: "translateX(-50%)",
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 9,
                  letterSpacing: "0.06em",
                  color: "#00C2A8",
                  padding: "3px 6px",
                  borderRadius: 3,
                  background: "rgba(0, 194, 168, 0.08)",
                  border: "1px solid rgba(0, 194, 168, 0.25)",
                  whiteSpace: "nowrap",
                  maxWidth: 200,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  backdropFilter: "blur(4px)",
                }}
                data-testid={`arrow-label-${node.id}`}
              >
                {node.arrowLabel}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile / stacked: arrow shown as chip below */}
      {!isLast && node.arrowLabel && (
        <div className="xl:hidden mt-3 mb-3 flex items-center justify-center gap-2">
          <ChevronsRight size={14} color="#00C2A8" />
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10,
              color: "#00C2A8",
              padding: "3px 8px",
              borderRadius: 4,
              background: "rgba(0, 194, 168, 0.08)",
              border: "1px solid rgba(0, 194, 168, 0.25)",
            }}
          >
            {node.arrowLabel}
          </span>
          <ChevronsRight size={14} color="#00C2A8" />
        </div>
      )}
    </div>
  );
}

function OrchestrationMap() {
  return (
    <article data-testid="orchestration-map" className="card-glass p-6">
      <header className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2.5">
          <div
            className="flex items-center justify-center rounded-md"
            style={{ width: 28, height: 28, background: "rgba(0, 194, 168, 0.10)", border: "1px solid rgba(0, 194, 168, 0.25)" }}
          >
            <Waypoints size={14} color="#00C2A8" />
          </div>
          <div>
            <h2 style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 15, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
              Live Orchestration Map
            </h2>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase" }}>
              5 agents · streaming handoffs · updated in real time
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Pill tone="teal" label="System operational" pulse />
        </div>
      </header>

      <div
        className="flex overflow-x-auto pb-4 pt-2 pl-2 pr-2"
        style={{
          gap: 68,
          scrollSnapType: "x mandatory",
        }}
      >
        {NODES.map((n, i) => (
          <div key={n.id} style={{ scrollSnapAlign: "start" }}>
            <AgentNode node={n} i={i} isLast={i === NODES.length - 1} />
          </div>
        ))}
      </div>
    </article>
  );
}

function LeadTrace() {
  return (
    <article data-testid="lead-trace" className="card-glass p-6">
      <header className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <div
            className="flex items-center justify-center rounded-md"
            style={{ width: 28, height: 28, background: "rgba(0, 194, 168, 0.10)", border: "1px solid rgba(0, 194, 168, 0.25)" }}
          >
            <Radio size={14} color="#00C2A8" />
          </div>
          <div>
            <h2 style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 15, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
              Live Lead Trace · Dr. Marcus Webb
            </h2>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase" }}>
              Watch one lead move through the system · Jun 17 → Jun 20
            </div>
          </div>
        </div>
        <div
          className="flex items-center gap-2"
          style={{
            padding: "4px 10px",
            borderRadius: 6,
            background: "rgba(0, 194, 168, 0.08)",
            border: "1px solid rgba(0, 194, 168, 0.25)",
          }}
        >
          <User size={12} color="#00C2A8" />
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10.5, color: "#00C2A8", letterSpacing: "0.06em" }}>
            Regional Surgical · WARM · Score 72
          </span>
        </div>
      </header>

      <div className="relative" style={{ paddingLeft: 24 }}>
        {/* Vertical rail */}
        <span
          style={{
            position: "absolute",
            left: 11,
            top: 8,
            bottom: 8,
            width: 2,
            background: "linear-gradient(180deg, #0EA5E9 0%, #00C2A8 40%, #F59E0B 60%, #22C55E 100%)",
            opacity: 0.35,
            borderRadius: 999,
          }}
        />

        <ul className="space-y-4">
          {TRACE.map((t, i) => {
            const { Icon } = t;
            return (
              <li
                key={t.id}
                data-testid={t.id}
                className="flex items-start gap-4 enter-up"
                style={{ animationDelay: `${i * 60}ms`, position: "relative" }}
              >
                {/* Dot */}
                <span
                  style={{
                    position: "absolute",
                    left: -18,
                    top: 8,
                    width: 12,
                    height: 12,
                    borderRadius: 999,
                    background: t.accent,
                    boxShadow: `0 0 0 3px rgba(15, 25, 35, 1), 0 0 0 4px ${t.accent}40`,
                    zIndex: 1,
                  }}
                />

                <div
                  className="flex-1 min-w-0 rounded-lg"
                  style={{
                    padding: "10px 14px",
                    background: t.final
                      ? `linear-gradient(135deg, ${t.accent}12 0%, transparent 100%), rgba(15, 25, 35, 0.5)`
                      : "rgba(15, 25, 35, 0.5)",
                    border: `1px solid ${t.final ? t.accent + "40" : "var(--border-divider)"}`,
                    borderLeft: `2px solid ${t.accent}`,
                  }}
                >
                  <div className="flex items-center gap-2.5 mb-1 flex-wrap">
                    <span
                      style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 10.5,
                        color: "var(--text-muted)",
                        letterSpacing: "0.06em",
                      }}
                    >
                      {t.time}
                    </span>
                    <span
                      className="inline-flex items-center gap-1.5"
                      style={{
                        padding: "2px 7px",
                        borderRadius: 4,
                        background: `${t.accent}14`,
                        border: `1px solid ${t.accent}30`,
                        color: t.accent,
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: 10,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        fontWeight: 600,
                      }}
                    >
                      <Icon size={10} />
                      {t.agent}
                    </span>
                    {t.auto && (
                      <span
                        style={{
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: 9,
                          color: "#7A9BB5",
                          padding: "1px 5px",
                          borderRadius: 3,
                          background: "rgba(122, 155, 181, 0.08)",
                          border: "1px solid rgba(122, 155, 181, 0.25)",
                          letterSpacing: "0.10em",
                        }}
                      >
                        AUTO-TRIGGERED
                      </span>
                    )}
                    {t.highlight && (
                      <span
                        style={{
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: 9,
                          color: "#22C55E",
                          padding: "1px 5px",
                          borderRadius: 3,
                          background: "rgba(34, 197, 94, 0.10)",
                          border: "1px solid rgba(34, 197, 94, 0.30)",
                          letterSpacing: "0.10em",
                        }}
                      >
                        HIGH-INTENT
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: 13,
                      lineHeight: 1.55,
                      color: t.tone === "muted" ? "var(--text-secondary)" : "var(--text-primary)",
                      fontWeight: t.final ? 600 : 500,
                    }}
                  >
                    {t.action}
                    {t.final && <CheckCircle2 size={13} color="#22C55E" style={{ marginLeft: 6, verticalAlign: "middle" }} />}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Summary strip */}
      <div
        className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 pt-5"
        style={{ borderTop: "1px dashed var(--border-divider)" }}
      >
        {[
          { k: "Total Time", v: "3 days 1h 10m", accent: "#00C2A8", Icon: Clock },
          { k: "Agent Touches", v: "9", accent: "#0EA5E9", Icon: Zap },
          { k: "Human Involvement", v: "0", accent: "#F59E0B", Icon: Users },
        ].map((s, i) => {
          const { Icon } = s;
          return (
            <div
              key={i}
              className="flex items-center gap-3 rounded-lg p-3"
              style={{
                background: "rgba(15, 25, 35, 0.4)",
                border: "1px solid var(--border-divider)",
              }}
            >
              <div
                className="flex items-center justify-center rounded-md"
                style={{ width: 30, height: 30, background: `${s.accent}12`, border: `1px solid ${s.accent}30` }}
              >
                <Icon size={13} color={s.accent} />
              </div>
              <div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase" }}>
                  {s.k}
                </div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, fontSize: 14, color: s.accent, letterSpacing: "-0.01em", marginTop: 1 }}>
                  {s.v}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </article>
  );
}

function HealthStats() {
  return (
    <section data-testid="health-stats" className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {HEALTH_STATS.map((s, i) => {
        const { Icon } = s;
        return (
          <article
            key={s.id}
            data-testid={s.id}
            className="card-glass card-interactive p-5 enter-up relative overflow-hidden"
            style={{
              animationDelay: `${i * 80}ms`,
              background: s.highlight
                ? `linear-gradient(135deg, ${s.accent}0F 0%, transparent 100%), var(--bg-elevated)`
                : "var(--bg-elevated)",
              borderLeft: `3px solid ${s.accent}`,
            }}
          >
            <div className="flex items-start justify-between mb-3">
              <div
                className="flex items-center justify-center rounded-lg"
                style={{ width: 34, height: 34, background: `${s.accent}12`, border: `1px solid ${s.accent}30` }}
              >
                <Icon size={16} color={s.accent} />
              </div>
              {s.highlight && (
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9,
                    letterSpacing: "0.14em",
                    color: s.accent,
                    padding: "3px 7px",
                    borderRadius: 4,
                    background: `${s.accent}18`,
                    border: `1px solid ${s.accent}40`,
                    textTransform: "uppercase",
                  }}
                >
                  {s.highlight}
                </span>
              )}
            </div>
            <div
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: 12,
                fontWeight: 500,
                color: "var(--text-secondary)",
                marginBottom: 8,
              }}
            >
              {s.label}
            </div>
            <div className="stat-value" style={{ color: s.accent }}>
              {s.value}
            </div>
            <div
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10.5,
                color: "var(--text-muted)",
                marginTop: 6,
                letterSpacing: "0.02em",
              }}
            >
              {s.note}
            </div>
          </article>
        );
      })}
    </section>
  );
}

/* --- Page --------------------------------------------------------- */

export default function Orchestration() {
  return (
    <div data-testid="orchestration-page" className="max-w-[1400px] mx-auto space-y-8">
      {/* Header */}
      <section data-testid="orchestration-header" className="enter-up">
        <div className="flex items-start gap-4">
          <div
            style={{
              width: 4,
              alignSelf: "stretch",
              background: "linear-gradient(180deg, #0EA5E9, #00C2A8, #F59E0B, #22C55E)",
              borderRadius: 4,
              minHeight: 68,
            }}
          />
          <div>
            <div
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                letterSpacing: "0.14em",
                color: "var(--accent-teal)",
                textTransform: "uppercase",
                marginBottom: 6,
              }}
            >
              Module · Multi-agent coordination
            </div>
            <h1
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 800,
                fontSize: 28,
                letterSpacing: "-0.02em",
                color: "var(--text-primary)",
                lineHeight: 1.2,
                marginBottom: 8,
              }}
            >
              Agent Orchestration
            </h1>
            <p
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: 14,
                lineHeight: 1.55,
                color: "var(--text-secondary)",
                maxWidth: 820,
              }}
            >
              Five agents. One coordinated system.{" "}
              <span style={{ color: "#00C2A8", fontWeight: 600 }}>
                No human involvement until demo is booked.
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* Orchestration Map */}
      <OrchestrationMap />

      {/* Live Lead Trace */}
      <LeadTrace />

      {/* Health stats */}
      <HealthStats />
    </div>
  );
}
